import React, { useState, useEffect } from 'react'; 
import { 
  TextField, 
  Button, 
  Box, 
  IconButton, 
  Typography, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Grid,
  Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { API_BASE_URL } from '../../../config/api';
import { useSnackbar } from 'notistack';
import ApiKeyManagement from './ApiKeyManagement'; // Import the new component
import ServerKeyManagement from './ServerKeyManagement';
import InstructionHistoryButton from './InstructionHistoryButton';
import useIsMobile from '../useIsMobile';
import './AssistantSettingsTheme.css';

// --- Utility Components ---
const TextFieldWithModal = ({ label, value, onChange, onSave, saving = false, headerAction = null }) => {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const getDisplayValue = () => {
    const fullValue = value || '';
    if (!fullValue) return '';

    const firstLine = fullValue.split('\n')[0];
    const maxLength = 100;

    if (fullValue.includes('\n') || firstLine.length > maxLength) {
      return firstLine.substring(0, maxLength) + '...';
    }
    return fullValue;
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
        <TextField
          label={label}
          value={getDisplayValue()}
          onClick={() => setOpen(true)}
          fullWidth
          InputProps={{
            readOnly: true,
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                {headerAction}
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(true);
                  }}
                >
                  <OpenInFullIcon />
                </IconButton>
              </Box>
            ),
          }}
        />
      </Box>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ className: "assistantDialogPaper" }}
      >
        <DialogTitle className="assistantDialogTitle">
          <Typography variant="h6">{label}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {headerAction}
            <IconButton onClick={() => setOpen(false)} size="small" className="assistantIconButtonNeutral">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <TextField
            value={value || ""}
            onChange={onChange}
            fullWidth
            multiline
            rows={isMobile ? 12 : 15}
            variant="outlined"
            sx={{ mt: 2 }}
            autoFocus={!isMobile}
          />
        </DialogContent>

        <DialogActions className="assistantDialogActions">
          <Button
            onClick={onSave}
            variant="contained"
            disabled={saving || !onSave}
            className="assistantPrimaryButton"
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>

          <Button onClick={() => setOpen(false)} variant="outlined" className="assistantOutlineButton">
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};
// --- GeneralSettings Component ---

const GeneralSettings = ({ assistant, onUpdate }) => {
  const [name, setName] = useState(assistant.name);
  const [greeting, setGreeting] = useState(assistant.greeting || '');
  const [instructions, setInstructions] = useState(assistant.instructions || '');
  const [notificationEmails, setNotificationEmails] = useState(assistant.notification_emails || []);
  const [notificationNumbers, setNotificationNumbers] = useState(assistant.notification_numbers || []);
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useIsMobile();

  // Sync state with props
  useEffect(() => {
    setName(assistant.name || '');
    setGreeting(assistant.greeting || '');
    setInstructions(assistant.instructions || '');
    setNotificationEmails(assistant.notification_emails || []);
    setNotificationNumbers(assistant.notification_numbers || []);
  }, [assistant]);

  // Handler for copying the ID
  const handleCopyId = () => {
    navigator.clipboard.writeText(assistant.id);
    enqueueSnackbar('Assistant ID copied!', { variant: 'info' });
  };
  
  // REVISED Instruction text for the API Tooltip
  const apiInstructions = `To use the Agent API:
1. **API Key:** Generate an API Key below in the "API Key Management" section.
2. **Endpoint:** Send a POST request to: ${API_BASE_URL}/{assistant_id}/chat
3. **Authentication:** Set the 'X-API-KEY' header to your key.

--- Response Modes ---
The API supports two modes via the 'stream' field in the JSON body:

4. **Streaming Mode (Default):** Omit the 'stream' field or set it to 'true'. Output is streamed as plain text chunks.
5. **Request/Response Mode:** Set 'stream' to 'false'. The server will wait for the entire response and return a single JSON object: {"assistant_id": "...", "conversation_id": "...", "response": "..."}

--- Conversation History ---
6. **To start a NEW conversation:** Omit the 'conversation_id' field.
7. **To continue a conversation:** Pass the 'conversation_id' from the previous response.`;

  // --- Notification Handlers (Unchanged) ---
  const handleAddEmail = () => {
    setNotificationEmails([...notificationEmails, '']);
  };

  const handleAddNumber = () => {
    setNotificationNumbers([...notificationNumbers, '']);
  };

  const handleEmailChange = (index, value) => {
    const newEmails = [...notificationEmails];
    newEmails[index] = value;
    setNotificationEmails(newEmails);
  };

  const handleNumberChange = (index, value) => {
    const newNumbers = [...notificationNumbers];
    newNumbers[index] = value;
    setNotificationNumbers(newNumbers);
  };

  const handleRemoveEmail = (index) => {
    setNotificationEmails(notificationEmails.filter((_, i) => i !== index));
  };

  const handleRemoveNumber = (index) => {
    setNotificationNumbers(notificationNumbers.filter((_, i) => i !== index));
  };

  // --- Submit Handler (Unchanged) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMessage('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/agents/${assistant.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name,
          greeting,
          instructions,
          notification_emails: notificationEmails.filter(email => email && email.trim()),
          notification_numbers: notificationNumbers.filter(number => number && number.trim())
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorText = errorData.detail || JSON.stringify(errorData);
        console.error('Server response:', response.status, errorText);
        setError(errorText || `Server error: ${response.status}`);
        throw new Error(`Server error: ${response.status}`);
      }

      await response.json();
      onUpdate();
      setSuccessMessage('Settings saved successfully!');
      enqueueSnackbar('Settings saved!', { variant: 'success' });

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating assistant:', error);
      if (!error.message.includes('Server error')) {
        setError('Failed to update assistant settings');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      className="assistantSettingsRoot"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
        </Alert>
      )}
      
      {/* Existing Settings Start Here */}
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        required
      />

      <Box sx={{ mt: 2 }}>
        <TextField
          label="General Greeting"
          value={greeting}
          onChange={(e) => setGreeting(e.target.value)}
          fullWidth
          multiline
          rows={2}
          helperText={isMobile ? undefined : 'Default greeting used by all channels unless overridden per channel.'}
          placeholder="Hi! How can I help you today?"
        />
      </Box>
            
      <TextFieldWithModal
        label="Instructions"
        value={instructions}
        onChange={(e) => setInstructions(e.target.value)}
        saving={saving}
        onSave={() => handleSubmit({ preventDefault: () => {} })}
        headerAction={
          <InstructionHistoryButton
            agentId={assistant.id}
            onRestore={(value) => setInstructions(value)}
          />
        }
      />

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Notification Emails</Typography>
        {notificationEmails.map((email, index) => (
          <Box key={index} className="assistantInlineRow" sx={{ mb: 1 }}>
            <TextField
              value={email}
              onChange={(e) => handleEmailChange(index, e.target.value)}
              placeholder="Enter email address"
              type="email"
              fullWidth
              size="small"
            />
          <IconButton onClick={() => handleRemoveEmail(index)} size="small" className="assistantIconButtonDanger">
            <DeleteIcon />
          </IconButton>
          </Box>
        ))}
      <Button onClick={handleAddEmail} variant="outlined" size="small" className="assistantOutlineButton">
        Add Email
      </Button>

      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Notification Phone Numbers</Typography>
        {notificationNumbers.map((number, index) => (
          <Box key={index} className="assistantInlineRow" sx={{ mb: 1 }}>
            <TextField
              value={number}
              onChange={(e) => handleNumberChange(index, e.target.value)}
              placeholder="Enter phone number"
              type="tel"
              fullWidth
              size="small"
            />
        <IconButton onClick={() => handleRemoveNumber(index)} size="small" className="assistantIconButtonDanger">
          <DeleteIcon />
        </IconButton>
          </Box>
        ))}

      <Button onClick={handleAddNumber} variant="outlined" size="small" className="assistantOutlineButton">
        Add Phone Number
      </Button>

      </Box>
      <Button
        type="submit"
        variant="contained"
        disabled={saving}
        className="assistantPrimaryButton assistantButtonAlignEnd"
      >
        {saving ? "Saving..." : "Save Changes"}
      </Button>

      {/* REVISED SECTION: Assistant ID and API Instructions */}
      <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1 }}>
        {!isMobile && (
          <Typography variant="caption" color="text.secondary">
            **API Integration Details**
          </Typography>
        )}

        <Grid container spacing={1} alignItems="center">

          {/* Assistant ID (Takes up most space, left-aligned) */}
          <Grid item xs={12} sm={8}>
            <TextField
              label="Assistant ID"
              value={assistant.id}
              fullWidth
              variant="standard"
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <Tooltip title="Copy Assistant ID">
                    <IconButton onClick={handleCopyId} edge="end" size="small">
                      <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                )
              }}
            />
          </Grid>

          {/* API usage tooltip is hover-only, so it is unreachable on touch, hide it there */}
          {!isMobile && (
            <Grid item xs={12} sm={4}>
              <Tooltip
                title={<Box sx={{ whiteSpace: 'pre-wrap', p: 1 }}>{apiInstructions}</Box>}
                placement="top-end"
                arrow
                PopperProps={{ disablePortal: true }}
              >
                <Button
                  variant="text"
                  color="info"
                  size="small"
                  sx={{ textTransform: 'none', ml: { xs: 0, sm: 1 }, mt: { xs: 1, sm: 0 } }}
                >
                  API Key & Usage Info
                </Button>
              </Tooltip>
            </Grid>
          )}
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />
      
      {/* ADDED: API Key Management Section */}
      <ApiKeyManagement />

      <Divider sx={{ my: 2 }} />

      <ServerKeyManagement />

    </Box>
  );
};

export default GeneralSettings;